import { getApiUrl } from "../utils/apiClient";

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

type JsonErrorShape = {
  success?: boolean;
  error?: string;
  message?: string;
  code?: string;
};

async function fetchWithRouteNotLoadedRetry(
  url: string,
  init: RequestInit = {},
): Promise<Response> {
  const maxExtraAttempts = 3;
  const backoffMs = [300, 550, 800];
  for (let attempt = 0; attempt <= maxExtraAttempts; attempt++) {
    const r = await fetch(url, { credentials: "include", ...init });
    if (r.status !== 503) return r;
    if (attempt === maxExtraAttempts) return r;
    try {
      const j = (await r.clone().json()) as JsonErrorShape;
      if (j.code !== "ROUTE_NOT_LOADED") return r;
    } catch {
      return r;
    }
    await sleep(backoffMs[attempt] ?? 500);
  }
  throw new Error("fetchWithRouteNotLoadedRetry: exhausted retries");
}

export class TrainingGeoAlertsApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "TrainingGeoAlertsApiError";
    this.status = status;
  }
}

export function isTrainingGeoAlertsUnauthorizedError(error: unknown): boolean {
  if (error instanceof TrainingGeoAlertsApiError) {
    return error.status === 401;
  }
  if (error instanceof Error) {
    return /(^|\s)401(\s|$)|unauthorized/i.test(error.message);
  }
  return false;
}

async function handle<T>(r: Response): Promise<T> {
  const j = (await r.json()) as {
    success?: boolean;
    error?: string;
    message?: string;
  };
  if (!r.ok || j.success === false) {
    throw new TrainingGeoAlertsApiError(
      j.error || j.message || `HTTP ${r.status}`,
      r.status,
    );
  }
  return j as T;
}

export type TeGeoState = {
  id: number;
  name: string | null;
  iso2: string | null;
  code: string | null;
};

export type TeGeoCounty = {
  id: number;
  name: string | null;
  state_id: number | null;
  code: string | null;
};

export type TrainingGeoAlertStateBlock = {
  geo_state_id: number;
  state_name: string | null;
  state_iso2: string | null;
  scope: "county";
  counties: { id: number; name: string | null }[];
  summary: string;
};

export async function getTrainingAlertStates() {
  const r = await fetchWithRouteNotLoadedRetry(
    getApiUrl("/api/events/reference/training-alert-states"),
  );
  return handle<{ success: boolean; data: TeGeoState[] }>(r);
}

export async function getTrainingAlertCounties(stateId: number) {
  const r = await fetchWithRouteNotLoadedRetry(
    getApiUrl(
      `/api/events/reference/training-alert-counties?state_id=${encodeURIComponent(String(stateId))}`,
    ),
  );
  return handle<{ success: boolean; data: TeGeoCounty[] }>(r);
}

export async function getMyTrainingGeoAlerts() {
  const r = await fetchWithRouteNotLoadedRetry(
    getApiUrl("/api/events/me/training-geo-alerts"),
  );
  return handle<{
    success: boolean;
    data: { states: TrainingGeoAlertStateBlock[]; email_enabled: boolean };
  }>(r);
}

export async function putMyTrainingGeoAlerts(body: {
  geo_state_id: number;
  county_ids: number[];
}) {
  const r = await fetchWithRouteNotLoadedRetry(
    getApiUrl("/api/events/me/training-geo-alerts"),
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  return handle<{
    success: boolean;
    data: { states: TrainingGeoAlertStateBlock[]; email_enabled: boolean };
  }>(r);
}

/**
 * Guest (email-first) signup. Omit `county_ids` for state-wide alerts, or pass counties
 * to narrow the subscription. The API answers 200 for both new and already-confirmed
 * emails, so the UI always shows "check your email".
 */
export async function subscribeGuestTrainingGeoAlerts(body: {
  email: string;
  geo_state_id: number;
  county_ids?: number[];
  source?: string;
}) {
  const r = await fetchWithRouteNotLoadedRetry(
    getApiUrl("/api/events/training-geo-alerts/guest/subscribe"),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  return handle<{ success: boolean }>(r);
}

export async function deleteMyTrainingGeoAlertsState(geoStateId: number) {
  const r = await fetchWithRouteNotLoadedRetry(
    getApiUrl(`/api/events/me/training-geo-alerts/${encodeURIComponent(String(geoStateId))}`),
    { method: "DELETE" },
  );
  return handle<{
    success: boolean;
    data: { states: TrainingGeoAlertStateBlock[]; email_enabled: boolean };
  }>(r);
}
